import React, { Dispatch } from 'react';
import { Grid, GridRow, GridColumn } from 'semantic-ui-react';
import Button from '@material-ui/core/Button';
import { AgGridReact } from 'ag-grid-react';
import i18next from 'i18next';
import { MenuDto } from 'src/services/Client';
import HATEOASClient from 'src/services/HATEOASClient';
import { connect } from 'react-redux';
import { AnyAction, compose } from 'redux';
import GeneralActions from 'src/redux/actions/GeneralActions';
import { IReduxState } from 'src/redux/IReduxState';
import { ColDef, RowStyle, GridReadyEvent, GridApi } from 'ag-grid-community';
import Select, { Option } from 'react-select';
import Search from '@material-ui/icons/Search';
import RotateLeft from '@material-ui/icons/RotateLeft';
import EditOutlined from '@material-ui/icons/EditOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import { orderBy } from 'lodash';
import { renderErrorToast, renderSuccessToast } from 'src/utils/FeedbackMessageUtils';
import TextTabModals from './TextTabModals';

type SortDirection = 'asc' | 'desc' | null;

interface ITextTabProps {
    client: HATEOASClient;
    baseLinks?: MenuDto;
    addLoadingMessage: (message: string) => void;
    removeLoadingMessage: (message: string) => void;
    t: i18next.TranslationFunction;
    ReceiveTranslationServiceAction: (translationService: any) => void;
    userDetails?: any;
}

interface ITextTabState {
    rowData: any[];
    searchQuery: string;
    language: string;
    category: string;
    textElements: any[];
    expandedPaths: string[];
    usagesData: { [textId: number]: any[] };
    sortField: string | null;
    sortDirection: SortDirection;
    // Edit dialog
    editingText?: any;
    editContent: string;
    isEditDialogOpen: boolean;
    isConfirmDialogOpen: boolean;
    isSavingEdit: boolean;
    // Row selection
    selectedRows: any[];
    // Merge
    isMergePreviewOpen: boolean;
    isMergeConfirmOpen: boolean;
    mergeCanonicalId: number | null;
    mergePlanToken: string | null;
    mergeConflictError: string | null;
    isMerging: boolean;
    // Search & Replace
    isSearchReplaceOpen: boolean;
    isSearchReplacePreviewOpen: boolean;
    isSearchReplaceConfirmOpen: boolean;
    searchTerm: string;
    replaceTerm: string;
    searchReplacePreviewData: any | null;
    searchReplacePlanToken: string | null;
    isSearchReplacing: boolean;
}

const SortableHeader = (props: any) => {
    const { displayName, field, getSort, onSort } = props;
    const { sortField, sortDirection } = getSort();
    const isActive = sortField === field;
    let arrow = '\u2195';
    if (isActive && sortDirection === 'asc') arrow = '\u2191';
    else if (isActive && sortDirection === 'desc') arrow = '\u2193';

    return (
        <div
            onClick={() => onSort(field)}
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                width: '100%',
                textAlign: 'center',
                color: isActive ? '#ffc107' : 'inherit',
                fontWeight: isActive ? 700 : 'inherit',
            }}
        >
            {displayName}
            <span style={{ fontSize: '12px', marginLeft: '2px' }}>{arrow}</span>
        </div>
    );
};

class TextTab extends React.Component<ITextTabProps, ITextTabState> {
    constructor(props: ITextTabProps) {
        super(props);
        this.state = {
            rowData: [],
            searchQuery: '',
            language: '',
            category: 'COMMENT',
            textElements: [],
            expandedPaths: [],
            usagesData: {},
            sortField: 'usageCount',
            sortDirection: 'desc',
            editContent: '',
            isEditDialogOpen: false,
            isConfirmDialogOpen: false,
            isSavingEdit: false,
            // Row selection
            selectedRows: [],
            // Merge
            isMergePreviewOpen: false,
            isMergeConfirmOpen: false,
            mergeCanonicalId: null,
            mergePlanToken: null,
            mergeConflictError: null,
            isMerging: false,
            // Search & Replace
            isSearchReplaceOpen: false,
            isSearchReplacePreviewOpen: false,
            isSearchReplaceConfirmOpen: false,
            searchTerm: '',
            replaceTerm: '',
            searchReplacePreviewData: null,
            searchReplacePlanToken: null,
            isSearchReplacing: false,
        };
    }

    gridApi?: GridApi;

    /**
     * Set to true to enable multi-row selection with the contextual toolbar.
     * Checkbox column is automatically added as the first column when enabled.
     */
    private readonly selectable = true;

    onGridReady = (event: GridReadyEvent) => {
        this.gridApi = event.api;
    }

    componentDidMount(): void {
        this.checkPrivilegesAndLoad();
    }

    componentDidUpdate(prevProps: ITextTabProps): void {
        if (!prevProps.userDetails && this.props.userDetails) {
            this.checkPrivilegesAndLoad();
        }
    }

    get hasPrivilege() {
        const { userDetails } = this.props;
        return (
            userDetails &&
            userDetails.privileges &&
            userDetails.privileges.some(
                (priv: any) =>
                    priv.identifier === 'ADMIN_AREA_READ' ||
                    priv.identifier === 'ADMIN_AREA_WRITE',
            )
        );
    }

    checkPrivilegesAndLoad = () => {
        if (this.hasPrivilege) {
            this.loadTexts();
        }
    }

    get languageOptions(): Option[] {
        const { t } = this.props;
        return [
            { value: '', label: t('GLOBAL:ALL') },
            { value: 'de', label: t('GLOBAL:LANG_DE') },
            { value: 'en', label: t('GLOBAL:LANG_EN') },
        ];
    }

    get categoryOptions(): Option[] {
        return [{ value: 'COMMENT', label: 'Comment' }];
    }

    getSortState = () => ({
        sortField: this.state.sortField,
        sortDirection: this.state.sortDirection,
    })

    handleSort = (field: string) => {
        this.setState(
            (prev: ITextTabState) => {
                let direction: SortDirection = 'asc';
                if (prev.sortField === field) {
                    if (prev.sortDirection === 'asc') direction = 'desc';
                    else if (prev.sortDirection === 'desc') direction = null;
                }
                return {
                    sortField: direction ? field : null,
                    sortDirection: direction,
                } as ITextTabState;
            },
            () => {
                this.rebuildRows();
                if (this.gridApi) this.gridApi.refreshHeader();
            },
        );
    }

    sortTextElements = (elements: any[]): any[] => {
        const { sortField, sortDirection } = this.state;
        if (!sortField || !sortDirection) return elements;
        return orderBy(elements, [(el: any) => el[sortField] || 0], [sortDirection]);
    }

    sortUsages = (usages: any[]): any[] => {
        // Sort model years numerically, ascending by default
        return orderBy(usages, [(usage: any) => Number(usage.modelYear) || 0], ['asc']);
    }

    sortTypeFamilies = (typeFamilies: any[]): any[] => {
        // Sort type families by name, ascending
        return orderBy(typeFamilies, [(tf: any) => tf.typeFamilyName || ''], ['asc']);
    }

    sortDerivatives = (derivatives: any[]): any[] => {
        // Sort derivatives by name, ascending
        return orderBy(derivatives, [(der: any) => der.derivativeName || ''], ['asc']);
    }

    toggleRow = (row: any) => {
        const { expandedPaths, usagesData } = this.state;
        const isExpanded = expandedPaths.includes(row.nodeId);

        if (isExpanded) {
            const newPaths = expandedPaths.filter((id) => id !== row.nodeId);
            this.setState({ expandedPaths: newPaths }, () => this.rebuildRows());
            return;
        }

        const nextPaths = [...expandedPaths, row.nodeId];

        // Text level: fetch usages drill-down once, then render
        if (row.level === 0 && !usagesData[row.objectId]) {
            this.setState({ expandedPaths: nextPaths }, () =>
                this.loadTextUsages(row.objectId),
            );
        } else {
            this.setState({ expandedPaths: nextPaths }, () => this.rebuildRows());
        }
    }

    loadTexts = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { searchQuery, language, category } = this.state;

        if (!baseLinks) return;

        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD');
        addLoadingMessage(loadingMessage);
        const params: any = { type: category, size: 0 };
        if (language) params.lang = language;
        if (searchQuery) params.query = encodeURIComponent(searchQuery);

        client
            .GET_texts(baseLinks)
            .execute(params)
            .then((response: any) => {
                removeLoadingMessage(loadingMessage);
                this.setState(
                    {
                        textElements: (response && response.elements) || [],
                        expandedPaths: [],
                        usagesData: {},
                    },
                    () => this.rebuildRows(),
                );
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                console.error('Failed to load texts', error);
            });
    }

    loadTextUsages = (textId: number) => {
        const { addLoadingMessage, removeLoadingMessage, t } = this.props;

        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD_USAGE');
        addLoadingMessage(loadingMessage);
        this.fetchBatchTextUsages([textId])
            .then((usagesByTextId) => {
                removeLoadingMessage(loadingMessage);
                this.setState(
                    (prev: ITextTabState) => ({
                        usagesData: {
                            ...prev.usagesData,
                            [textId]: usagesByTextId[textId] || [],
                        },
                    }),
                    () => this.rebuildRows(),
                );
            });
    }

    clearFilters = () => {
        this.setState(
            {
                searchQuery: '',
                language: '',
                category: 'COMMENT',
            },
            () => this.loadTexts(),
        );
    }

    // ── Row selection ──────────────────────────────────────────────────────────

    onSelectionChanged = (event: any) => {
        const nodes = event.api.getSelectedNodes();
        const rows = nodes
            .map((node: any) => node.data)
            .filter((data: any) => data && data.level === 0);
        this.setState({ selectedRows: rows });
    }

    clearSelection = () => {
        if (this.gridApi) { this.gridApi.deselectAll(); }
        this.setState({ selectedRows: [] });
    }

    normalizeUsageModelYears = (usageEntry: any): any[] => {
        if (Array.isArray(usageEntry)) {
            return usageEntry;
        }
        if (Array.isArray(usageEntry && usageEntry.modelYears)) {
            return usageEntry.modelYears;
        }
        if (Array.isArray(usageEntry && usageEntry.elements)) {
            return usageEntry.elements;
        }
        return [];
    }

    mapBatchUsagesResponse = (response: any, fallbackTextIds: number[]): { [textId: number]: any[] } => {
        const mappedUsages: { [textId: number]: any[] } = {};

        if (Array.isArray(response)) {
            response.forEach((entry: any, index: number) => {
                const textId = entry && (entry.textId || entry.objectId || entry.id || entry.textObjectId || (entry.textObject && entry.textObject.objectId));
                const resolvedTextId = typeof textId === 'number' ? textId : fallbackTextIds[index];
                if (resolvedTextId !== undefined) {
                    mappedUsages[resolvedTextId] = this.normalizeUsageModelYears(entry.usages || entry);
                }
            });
        } else if (response && typeof response === 'object') {
            fallbackTextIds.forEach((textId) => {
                const usageEntry = response[textId] || response[String(textId)];
                mappedUsages[textId] = this.normalizeUsageModelYears((usageEntry && usageEntry.usages) || usageEntry);
            });
        }

        fallbackTextIds.forEach((textId) => {
            if (!mappedUsages[textId]) {
                mappedUsages[textId] = [];
            }
        });

        return mappedUsages;
    }

    fetchBatchTextUsages = (textIds: number[]): Promise<{ [textId: number]: any[] }> => {
        const { client, baseLinks } = this.props;
        if (!baseLinks || textIds.length === 0) {
            return Promise.resolve({});
        }

        return client
            .GET_textUsagesBatch(baseLinks)
            .execute(textIds)
            .then((response: any) => this.mapBatchUsagesResponse(response, textIds))
            .catch((error: any) => {
                console.error('Failed to load text usages', error);
                const emptyUsages: { [textId: number]: any[] } = {};
                textIds.forEach((textId) => {
                    emptyUsages[textId] = [];
                });
                return emptyUsages;
            });
    }

    // ── Merge handlers ─────────────────────────────────────────────────────────

    fetchMergePlanToken = (canonicalId: number) => {
        const { client, baseLinks } = this.props;
        const { selectedRows } = this.state;
        if (!baseLinks) { return; }

        const mergeIds = selectedRows.map((r) => r.objectId);

        // Call merge API with dryRun=true to get token
        client
            .POST_mergeTexts(baseLinks)
            .execute({ keepId: canonicalId, mergeIds, dryRun: true })
            .then((response: any) => {
                const token = response && response.mergePlanToken ? response.mergePlanToken : null;
                this.setState({ mergePlanToken: token });
            })
            .catch((error: any) => {
                console.error('Failed to fetch merge plan token', error);
            });
    }

    openMergePreview = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { selectedRows, usagesData } = this.state;
        if (selectedRows.length < 2) { return; }

        const canonical = selectedRows.reduce((best, r) =>
            (r.usageCount || 0) > (best.usageCount || 0) ? r : best,
        );

        // Fetch usages for any selected row not yet expanded/loaded
        const rowsToLoad = selectedRows.filter((r) => !usagesData[r.objectId]);

        if (rowsToLoad.length === 0 || !baseLinks) {
            this.setState({ isMergePreviewOpen: true, mergeCanonicalId: canonical.objectId });
            this.fetchMergePlanToken(canonical.objectId);
            return;
        }

        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD_USAGE');
        addLoadingMessage(loadingMessage);
        this.fetchBatchTextUsages(rowsToLoad.map((row) => row.objectId as number)).then((batchedUsages) => {
            removeLoadingMessage(loadingMessage);
            const newUsagesData = { ...this.state.usagesData };
            rowsToLoad.forEach((row) => {
                newUsagesData[row.objectId] = batchedUsages[row.objectId] || [];
            });
            this.setState({
                usagesData: newUsagesData,
                isMergePreviewOpen: true,
                mergeCanonicalId: canonical.objectId,
            });
            this.fetchMergePlanToken(canonical.objectId);
        });
    }

    closeMergePreview = () => {
        this.setState({ isMergePreviewOpen: false, mergeCanonicalId: null, mergePlanToken: null });
    }

    openMergeConfirm = () => {
        this.setState({ isMergePreviewOpen: false, isMergeConfirmOpen: true });
    }

    closeMergeConfirm = () => {
        this.setState({ isMergeConfirmOpen: false, isMergePreviewOpen: true });
    }

    closeMergeAll = () => {
        this.setState({ isMergePreviewOpen: false, isMergeConfirmOpen: false, mergeCanonicalId: null, mergePlanToken: null, mergeConflictError: null });
    }

    refreshMergeUsageData = () => {
        const { baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { selectedRows, usagesData } = this.state;
        if (!baseLinks) { return; }

        const rowsToLoad = selectedRows.filter((r) => !usagesData[r.objectId]);

        if (rowsToLoad.length === 0) {
            // All data already loaded, just clear conflict error
            this.setState({ mergeConflictError: null });
            return;
        }

        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD_USAGE');
        addLoadingMessage(loadingMessage);
        this.fetchBatchTextUsages(rowsToLoad.map((row) => row.objectId as number)).then((batchedUsages) => {
            removeLoadingMessage(loadingMessage);
            const newUsagesData = { ...this.state.usagesData };
            rowsToLoad.forEach((row) => {
                newUsagesData[row.objectId] = batchedUsages[row.objectId] || [];
            });
            this.setState({
                usagesData: newUsagesData,
                mergeConflictError: null,
            });
        });
    }

    onMergeCanonicalChange = (canonicalId: number) => {
        const { baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { selectedRows } = this.state;
        if (!baseLinks) { return; }

        // Always reload usage data for all selected rows when radio button changes
        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD_USAGE');
        addLoadingMessage(loadingMessage);
        this.fetchBatchTextUsages(selectedRows.map((row) => row.objectId as number)).then((batchedUsages) => {
            removeLoadingMessage(loadingMessage);
            const newUsagesData: { [textId: number]: any[] } = {};
            selectedRows.forEach((row) => {
                newUsagesData[row.objectId] = batchedUsages[row.objectId] || [];
            });
            this.setState({
                mergeCanonicalId: canonicalId,
                usagesData: newUsagesData,
            });
            this.fetchMergePlanToken(canonicalId);
        });
    }

    executeMerge = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { mergeCanonicalId, selectedRows, mergePlanToken } = this.state;
        if (!baseLinks || !mergeCanonicalId) { return; }

        const mergeIds = selectedRows.map((r) => r.objectId);
        const loadingMessage = 'Merging texts';
        this.setState({ isMerging: true });
        addLoadingMessage(loadingMessage);

        const mergePayload: any = { keepId: mergeCanonicalId, mergeIds, dryRun: false };
        if (mergePlanToken) {
            mergePayload.expectedMergePlanToken = mergePlanToken;
        }
        client
            .POST_mergeTexts(baseLinks)
            .execute(mergePayload)
            .then(() => {
                removeLoadingMessage(loadingMessage);
                renderSuccessToast(
                    {
                        message: 'MANAGEMENT_VIEW:TEXT_SUCCESS',
                        details: `Text-ID ${mergeIds.filter((id) => id !== mergeCanonicalId).join(', ')} merged into Text-ID ${mergeCanonicalId}.`,
                        translatedDetails: true,
                        timestamp: Date.now(),
                    },
                    7000,
                );
                this.setState({ isMergeConfirmOpen: false, isMergePreviewOpen: false, mergeCanonicalId: null, mergePlanToken: null, isMerging: false, mergeConflictError: null });
                this.clearSelection();
                this.loadTexts();
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                if (error && (error.status === 409 || error.statusCode === 409)) {
                    this.setState({
                        isMerging: false,
                        isMergeConfirmOpen: false,
                        isMergePreviewOpen: false,
                        mergeCanonicalId: null,
                        mergePlanToken: null,
                        mergeConflictError: null,
                    });
                    renderErrorToast({
                        message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                        details: 'MANAGEMENT_VIEW:TEXT_MERGE_FAILURE',
                        timestamp: Date.now(),
                    });
                } else {
                    this.setState({ isMerging: false });
                    renderErrorToast({
                        message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                        details: error,
                        timestamp: Date.now(),
                    });
                    console.error('Failed to merge texts', error);
                }
            });
    }

    // ── Search & Replace handlers ──────────────────────────────────────────────

    openSearchReplace = () => {
        this.setState({ isSearchReplaceOpen: true, searchTerm: '', replaceTerm: '', searchReplacePreviewData: null, searchReplacePlanToken: null });
    }

    closeSearchReplace = () => {
        this.setState({ isSearchReplaceOpen: false });
    }

    openSearchReplacePreview = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { selectedRows, searchTerm, replaceTerm } = this.state;
        if (!baseLinks || !searchTerm.trim()) {
            return;
        }

        const loadingMessage = t('MANAGEMENT_VIEW:TEXT_LOAD_USAGE');
        addLoadingMessage(loadingMessage);
        client
            .POST_bulkReplaceTexts(baseLinks)
            .execute({
                textIds: selectedRows.map((row) => row.objectId),
                search: searchTerm,
                replace: replaceTerm,
                dryRun: true,
            })
            .then((response: any) => {
                removeLoadingMessage(loadingMessage);
                this.setState({
                    searchReplacePreviewData: response,
                    searchReplacePlanToken: response && response.replacePlanToken ? response.replacePlanToken : null,
                    isSearchReplaceOpen: false,
                    isSearchReplacePreviewOpen: true,
                });
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                renderErrorToast({
                    message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                    details: error,
                    timestamp: Date.now(),
                });
                console.error('Failed to preview search replace', error);
            });
    }

    closeSearchReplacePreview = () => {
        this.setState({ isSearchReplacePreviewOpen: false, isSearchReplaceOpen: true });
    }

    openSearchReplaceConfirm = () => {
        this.setState({ isSearchReplacePreviewOpen: false, isSearchReplaceConfirmOpen: true });
    }

    closeSearchReplaceConfirm = () => {
        this.setState({ isSearchReplaceConfirmOpen: false, isSearchReplacePreviewOpen: true });
    }

    closeSearchReplaceAll = () => {
        this.setState({
            isSearchReplaceOpen: false,
            isSearchReplacePreviewOpen: false,
            isSearchReplaceConfirmOpen: false,
            searchTerm: '',
            replaceTerm: '',
            searchReplacePreviewData: null,
            searchReplacePlanToken: null,
            isSearchReplacing: false,
        });
    }

    executeSearchReplace = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage } = this.props;
        const { selectedRows, searchTerm, replaceTerm, searchReplacePlanToken } = this.state;
        if (!baseLinks) { return; }

        const loadingMessage = 'Replacing texts';
        this.setState({ isSearchReplacing: true });
        addLoadingMessage(loadingMessage);

        const payload: any = {
            textIds: selectedRows.map((row) => row.objectId),
            search: searchTerm,
            replace: replaceTerm,
            dryRun: false,
        };
        if (searchReplacePlanToken) {
            payload.expectedReplacePlanToken = searchReplacePlanToken;
        }

        client
            .POST_bulkReplaceTexts(baseLinks)
            .execute(payload)
            .then((response: any) => {
                removeLoadingMessage(loadingMessage);
                renderSuccessToast(
                    {
                        message: 'MANAGEMENT_VIEW:TEXT_SUCCESS',
                        details: `${response && typeof response.changedTextCount === 'number' ? response.changedTextCount : selectedRows.length} text(s) updated.
                        ${response && typeof response.affectedUsageCount === 'number' ? response.affectedUsageCount : 0} usage(s) affected.`,
                        translatedDetails: true,
                        timestamp: Date.now(),
                    },
                    7000,
                );
                this.setState({
                    isSearchReplacePreviewOpen: false,
                    isSearchReplaceConfirmOpen: false,
                    searchTerm: '',
                    replaceTerm: '',
                    searchReplacePreviewData: null,
                    searchReplacePlanToken: null,
                    isSearchReplacing: false,
                });
                this.clearSelection();
                this.loadTexts();
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                if (error && (error.status === 409 || error.statusCode === 409)) {
                    this.setState({
                        isSearchReplaceOpen: false,
                        isSearchReplacePreviewOpen: false,
                        isSearchReplaceConfirmOpen: false,
                        searchReplacePreviewData: null,
                        searchReplacePlanToken: null,
                        isSearchReplacing: false,
                    });
                    renderErrorToast({
                        message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                        details: 'MANAGEMENT_VIEW:TEXT_MERGE_FAILURE',
                        timestamp: Date.now(),
                    });
                } else {
                    this.setState({ isSearchReplacing: false });
                    renderErrorToast({
                        message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                        details: error,
                        timestamp: Date.now(),
                    });
                    console.error('Failed to replace texts', error);
                }
            });
    }

    // ── Toolbar render ─────────────────────────────────────────────────────────

    renderSelectionToolbar = () => {
        const { t } = this.props;
        const { selectedRows } = this.state;
        const canMerge = selectedRows.length >= 2;
        return (
            <div className="text-tab-toolbar">
                <span className="text-tab-toolbar-count">{selectedRows.length} {t('MANAGEMENT_VIEW:MERGE_TEXTS_SELECTED')}</span>
                <div className="text-tab-toolbar-actions">
                    <Button
                        variant="outlined"
                        disabled={!canMerge}
                        onClick={this.openMergePreview}
                        style={{ fontSize: '13px', height: '32px', padding: '0 12px' }}
                    >
                        {t('MANAGEMENT_VIEW:MERGE_ACTION')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={this.openSearchReplace}
                        style={{ fontSize: '13px', height: '32px', padding: '0 12px' }}
                    >
                        {t('MANAGEMENT_VIEW:TEXT_SEARCH_REPLACE')}
                    </Button>
                </div>
                <span
                    className="text-tab-toolbar-dismiss"
                    onClick={this.clearSelection}
                    title={t('MANAGEMENT_VIEW:TEXT_CLEAR')}
                >
                    ✕
                </span>
            </div>
        );
    }

    openEditDialog = (row: any) => {
        this.setState({
            editingText: row,
            editContent: row && row.content ? String(row.content) : '',
            isEditDialogOpen: true,
            isConfirmDialogOpen: false,
        });
    }

    closeEditFlow = () => {
        this.setState({
            editingText: undefined,
            editContent: '',
            isEditDialogOpen: false,
            isConfirmDialogOpen: false,
            isSavingEdit: false,
        });
    }

    openConfirmDialog = () => {
        if (!this.isEditChanged()) return;
        this.setState({
            isEditDialogOpen: false,
            isConfirmDialogOpen: true,
        });
    }

    backToEditDialog = () => {
        this.setState({
            isEditDialogOpen: true,
            isConfirmDialogOpen: false,
        });
    }

    isEditChanged = () => {
        const { editingText, editContent } = this.state;
        if (!editingText) return false;
        const currentContent = editingText.content ? String(editingText.content).trim() : '';
        const nextContent = editContent.trim();
        return nextContent.length > 0 && nextContent !== currentContent;
    }

    saveEditedText = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { editingText, editContent } = this.state;
        if (!baseLinks || !editingText) return;

        const nextContent = editContent.trim();
        if (!nextContent) return;

        const loadingMessage = 'Saving text';
        this.setState({ isSavingEdit: true });
        addLoadingMessage(loadingMessage);

        client
            .PATCH_texts(baseLinks, editingText.objectId)
            .execute({
                content: nextContent,
            })
            .then(() => {
                removeLoadingMessage(loadingMessage);
                renderSuccessToast(
                  {
                    message: 'MANAGEMENT_VIEW:TEXT_SUCCESS',
                    details: `Text ID ${editingText.objectId} - ${t('MANAGEMENT_VIEW:TEXT_SUCCESS')}`,
                    translatedDetails: true,
                    timestamp: Date.now(),
                  },
                  7000,
                );
                this.closeEditFlow();
                this.loadTexts();
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                this.setState({ isSavingEdit: false });
                renderErrorToast({
                    message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                    details: error,
                    timestamp: Date.now(),
                });
                console.error('Failed to update text', error);
            });
    }

    rebuildRows = () => {
        this.setState({ rowData: this.buildDisplayRows() });
    }

    buildDisplayRows = (): any[] => {
        const { textElements, expandedPaths, usagesData } = this.state;
        const rows: any[] = [];

        this.sortTextElements(textElements).forEach((text: any) => {
            const textNodeId = `text-${text.objectId}`;
            rows.push({
                ...text,
                nodeId: textNodeId,
                level: 0,
                expandable: (text.usageCount || 0) > 0,
                displayContent: text.content,
                occurrences: text.usageCount,
                iNumber: '',
            });

            if (!expandedPaths.includes(textNodeId)) return;

            const usages = usagesData[text.objectId] || [];
            this.sortUsages(usages).forEach((year: any) => {
                const yearNodeId = `my-${text.objectId}-${year.modelYearId}`;
                rows.push({
                    nodeId: yearNodeId,
                    level: 1,
                    expandable: (year.typeFamilies || []).length > 0,
                    displayContent: year.modelYear,
                    occurrences: '',
                    iNumber: '',
                });

                if (!expandedPaths.includes(yearNodeId)) return;

                this.sortTypeFamilies(year.typeFamilies || []).forEach((tf: any) => {
                    const tfNodeId = `tf-${text.objectId}-${year.modelYearId}-${tf.typeFamilyId}`;
                    rows.push({
                        nodeId: tfNodeId,
                        level: 2,
                        expandable: (tf.derivatives || []).length > 0,
                        displayContent: tf.typeFamilyName,
                        occurrences: '',
                        iNumber: '',
                    });

                    if (!expandedPaths.includes(tfNodeId)) return;

                    this.sortDerivatives(tf.derivatives || []).forEach((der: any, index: number) => {
                        rows.push({
                            nodeId: `der-${text.objectId}-${year.modelYearId}-${tf.typeFamilyId}-${der.derivativeId || index}`,
                            level: 3,
                            expandable: false,
                            displayContent: der.derivativeName && der.btyp
                                ? `${der.derivativeName} (${der.btyp})`
                                : (der.derivativeName || '—'),
                            occurrences: '',
                            iNumber: Array.isArray(der.inumbers) ? der.inumbers.join(', ') : '',
                        });
                    });
                });
            });
        });

        return rows;
    }

    get columnDefinitions(): ColDef[] {
        const { t } = this.props;
        const { expandedPaths } = this.state;
        const colorByLevel: { [key: number]: string } = {
            0: '#1565c0',
            1: '#2e7d32',
            2: '#e65100',
            3: '#6a1b9a',
        };

        return [
            // Checkbox column — only rendered when selectable is enabled
            ...(this.selectable ? [{
                field: '',
                width: 48,
                maxWidth: 48,
                sortable: false,
                resizable: false,
                suppressMenu: true,
                checkboxSelection: (params: any) => params.data && params.data.level === 0,
                headerCheckboxSelection: false,
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            }] : []),
            {
                field: 'occurrences',
                headerName: t('TEXT_TOTAL'),
                width: 150,
                sortable: false,
                cellStyle: { fontWeight: 600 },
                headerComponent: SortableHeader,
                headerComponentParams: {
                    field: 'usageCount',
                    getSort: this.getSortState,
                    onSort: this.handleSort,
                },
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.occurrences : '',
            },
            {
                field: 'category',
                headerName: t('TEXT_CATEGORY'),
                width: 110,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.category : '',
            },
            {
                field: 'objectId',
                headerName: t('TEXT_ID'),
                width: 90,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.objectId : '',
            },
            {
                headerName: t('TEXT_CONTENT'),
                field: 'displayContent',
                flex: 1,
                minWidth: 280,
                cellRenderer: (params: any) => {
                    if (!params.data) return null;
                    const { level, expandable, nodeId, displayContent } = params.data;
                    const isOpen = expandedPaths.includes(nodeId);
                    const text = (displayContent === null || displayContent === undefined)
                        ? ''
                        : String(displayContent);
                    const singleLine = text.replace(/\s+/g, ' ').trim();

                    return (
                        <div
                            title={text}
                            className="text-content-cell"
                            style={{
                                cursor: expandable ? 'pointer' : 'default',
                                paddingLeft: level * 22,
                                color: colorByLevel[level],
                                fontWeight: level === 0 ? 500 : 400,
                            }}
                            onClick={() => expandable && this.toggleRow(params.data)}
                        >
                            {expandable ? (
                                <span style={{ marginRight: 8, fontSize: '9px' }}>
                                    {isOpen ? '▼' : '▶'}
                                </span>
                            ) : (
                                <span className="text-content-cell-placeholder" />
                            )}
                            <span
                                className="text-content-cell-text"
                                style={{
                                    paddingRight: level === 0 ? '8px' : '0',
                                }}
                            >
                                {singleLine}
                            </span>
                            {level === 0 ? (
                                <Tooltip title={t('MANAGEMENT_VIEW:TEXT_EDIT_CONTENT')} placement="top">
                                    <span
                                        className="text-edit-icon-action"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            this.openEditDialog(params.data);
                                        }}
                                    >
                                        <EditOutlined style={{ fontSize: 16 }} />
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                field: 'iNumber',
                headerName: t("TEXT_I_NUMBER"),
                width: 150,
                cellRenderer: (params: any) => {
                    const value =
                        params.data && params.data.level === 3 && params.data.iNumber
                            ? String(params.data.iNumber)
                            : '';

                    return (
                        <div
                            title={value}
                            className="text-inumber-cell"
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                field: 'modelYearCount',
                headerName: t('BUSINESS_APPROVAL_MODEL_YEAR'),
                width: 115,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.modelYearCount : '',
            },
            {
                field: 'typeFamilyCount',
                headerName:  t("BUSINESS_APPROVAL_FAMILY"),
                width: 115,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.typeFamilyCount : '',
            },
            {
                field: 'derivativeCount',
                headerName: t('TEXT_DERIVATES'),
                width: 95,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.derivativeCount : '',
            },
            {
                field: 'langCode',
                headerName: t('TEXT_LANGUAGE'),
                width: 95,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0
                        ? '-'
                        : '',
            },
        ];
    }

    getRowStyle = (params: any): RowStyle | undefined => {
        if (!params.data) return undefined;

        const bgByLevel: { [key: number]: string } = {
            0: '#ffffff',
            1: '#e3f2fd',
            2: '#f1f8e9',
            3: '#fff3e0',
        };

        return {
            background: bgByLevel[params.data.level] || '#ffffff',
            borderBottom: params.data.level === 0 ? '1px solid #ddd' : '1px solid #eee',
        };
    }

    render() {
        const { t } = this.props;
        if (!this.hasPrivilege) {
            return (
                <Grid className="managementview_container">
                    <GridRow centered={true} style={{ marginTop: '50px' }}>
                        <GridColumn width={8} textAlign="center">
                            <div
                                className="text-tab-no-access-card"
                            >
                                <i
                                    className="lock icon"
                                    style={{ fontSize: '3rem', color: '#999' }}
                                />
                                <h3 style={{ color: '#666', margin: '5px' }}>
                                    {t('BUSINESS_APPROVAL_RESTRICTION')}
                                </h3>
                                <p style={{ marginTop: '10px', color: '#666' }}>
                                    {t('BUSINESS_APPROVAL_ACCESS_MESSAGE')}
                                </p>
                            </div>
                        </GridColumn>
                    </GridRow>
                </Grid>
            );
        }

        const selectedLanguage =
            this.languageOptions.find((opt) => opt.value === this.state.language);
        const selectedCategory =
            this.categoryOptions.find((opt) => opt.value === this.state.category);

        return (
            <Grid className="managementview_container">
                <GridColumn width={4} className="job_title">
                    {t('TEXT_TTTLE')}
                </GridColumn>
                <GridRow columns={16} className="grid_header" style={{ paddingRight: '0' }}>
                    <GridColumn width={16}>
                        <div className="text-tab-filter-bar">
                            <div
                                className="filter_dropdown text-tab-search-dropdown"
                            >
                                <label className="grid_label">{t('BUSINESS_APPROVAL_SEARCH')}</label>
                                <input
                                    type="text"
                                    className="date-picker-input text-tab-search-input"
                                    value={this.state.searchQuery}
                                    placeholder={t('TEXT_SEARCH_PLACEHOLDER')}
                                    onChange={(event) =>
                                        this.setState({ searchQuery: event.target.value })
                                    }
                                    onKeyPress={(event) => {
                                        if (event.key === 'Enter') this.loadTexts();
                                    }}
                                />
                            </div>

                            <div
                                className="filter_dropdown text-tab-filter-select"
                            >
                                <label className="grid_label">{t('TEXT_LANGUAGE')}</label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        clearable={false}
                                        searchable={false}
                                        value={selectedLanguage}
                                        options={this.languageOptions}
                                        onChange={(selected: Option | null) =>
                                            this.setState({
                                                language: selected ? (selected.value as string) : '',
                                            })
                                        }
                                        placeholder={t('TEXT_LANGUAGE')}
                                    />
                                </div>
                            </div>

                            <div
                                className="filter_dropdown text-tab-filter-select"
                            >
                                <label className="grid_label">{t('TEXT_CATEGORY')}</label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        clearable={false}
                                        searchable={false}
                                        value={selectedCategory}
                                        options={this.categoryOptions}
                                        onChange={(selected: Option | null) =>
                                            this.setState({
                                                category: selected
                                                    ? (selected.value as string)
                                                    : 'COMMENT',
                                            })
                                        }
                                        placeholder={t('TEXT_CATEGORY')}
                                    />
                                </div>
                            </div>

                            <div className="text-tab-filter-actions">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Search />}
                                    onClick={this.loadTexts}
                                    className="text-tab-filter-btn"
                                >
                                    {t('BUSINESS_APPROVAL_SEARCH')}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<RotateLeft />}
                                    onClick={this.clearFilters}
                                    className="text-tab-filter-btn"
                                >
                                    {t('RESET')}
                                </Button>
                            </div>
                        </div>
                    </GridColumn>
                </GridRow>

                <GridRow>
                    <GridColumn width={16}>
                        {this.selectable && this.state.selectedRows.length > 0 && this.renderSelectionToolbar()}
                        <div
                            className="ag-theme-alpine"
                            style={{ height: 500, width: '100%' }}
                        >
                            <AgGridReact
                                rowData={this.state.rowData}
                                columnDefs={this.columnDefinitions}
                                headerHeight={40}
                                rowHeight={45}
                                animateRows={true}
                                getRowStyle={this.getRowStyle}
                                getRowId={(params: any) => params.data.nodeId}
                                onGridReady={this.onGridReady}
                                rowSelection={this.selectable ? 'multiple' : undefined}
                                onSelectionChanged={this.selectable ? this.onSelectionChanged : undefined}
                                suppressRowClickSelection={true}
                            />
                        </div>
                    </GridColumn>
                </GridRow>
                <TextTabModals
                    t={t}
                    // Edit
                    editingText={this.state.editingText}
                    editContent={this.state.editContent}
                    isEditDialogOpen={this.state.isEditDialogOpen}
                    isConfirmDialogOpen={this.state.isConfirmDialogOpen}
                    isSavingEdit={this.state.isSavingEdit}
                    isEditChanged={this.isEditChanged()}
                    onEditContentChange={(v) => this.setState({ editContent: v })}
                    onEditClose={this.closeEditFlow}
                    onEditContinue={this.openConfirmDialog}
                    onEditBack={this.backToEditDialog}
                    onEditSave={this.saveEditedText}
                    // Merge
                    selectedRows={this.state.selectedRows}
                    usagesData={this.state.usagesData}
                    isMergePreviewOpen={this.state.isMergePreviewOpen}
                    isMergeConfirmOpen={this.state.isMergeConfirmOpen}
                    mergeCanonicalId={this.state.mergeCanonicalId}
                    isMerging={this.state.isMerging}
                    onMergeCanonicalChange={this.onMergeCanonicalChange}
                    onMergePreviewClose={this.closeMergeAll}
                    onMergePreviewConfirm={this.openMergeConfirm}
                    onMergeConfirmBack={this.closeMergeConfirm}
                    onMergeConfirmClose={this.closeMergeAll}
                    onMergeExecute={this.executeMerge}
                    // Search & Replace
                    isSearchReplaceOpen={this.state.isSearchReplaceOpen}
                    isSearchReplacePreviewOpen={this.state.isSearchReplacePreviewOpen}
                    isSearchReplaceConfirmOpen={this.state.isSearchReplaceConfirmOpen}
                    searchTerm={this.state.searchTerm}
                    replaceTerm={this.state.replaceTerm}
                    searchReplacePreviewData={this.state.searchReplacePreviewData}
                    isSearchReplacing={this.state.isSearchReplacing}
                    onSearchTermChange={(v) => this.setState({ searchTerm: v })}
                    onReplaceTermChange={(v) => this.setState({ replaceTerm: v })}
                    onSearchReplaceClose={this.closeSearchReplaceAll}
                    onSearchReplacePreview={this.openSearchReplacePreview}
                    onSearchReplacePreviewBack={this.closeSearchReplacePreview}
                    onSearchReplacePreviewConfirm={this.openSearchReplaceConfirm}
                    onSearchReplaceConfirmBack={this.closeSearchReplaceConfirm}
                    onSearchReplaceConfirmClose={this.closeSearchReplaceAll}
                    onSearchReplacePreviewClose={this.closeSearchReplaceAll}
                    onSearchReplaceExecute={this.executeSearchReplace}
                />
            </Grid>
        );
    }
}

const mapStateToProps = (state: IReduxState) => ({
    userDetails: state.general.userDetails,
    baseLinks: state.general.baseLinks,
    client: state.general.client,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    addLoadingMessage: (message: string) => dispatch(GeneralActions.AddLoadingMessage(message)),
    removeLoadingMessage: (message: string) => dispatch(GeneralActions.RemoveLoadingMessage(message)),
    ReceiveTranslationServiceAction: (payload: any) => dispatch(GeneralActions.ReceiveTranslationServiceAction(payload))
});

export default compose<React.ComponentType<any>>(
    connect(mapStateToProps, mapDispatchToProps)
)(TextTab);



// WEBPACK FOOTER //
// ./src/views/ManagementView/TextTab.tsx